import React, { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiImage } from "react-icons/fi";
import { filterTruckLogos, getTruckLogoLabel, isTruckLogoAllowed } from "../lib/truckLogos";
import "./TruckLogoDropdown.css";

function LogoThumbnail({ file, logoUrl, dap }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [file, dap]);
  return (
    <span className="truck-logo-dropdown-thumbnail" aria-hidden="true">
      {file && !failed
        ? <img src={logoUrl(file, dap)} alt="" onError={() => setFailed(true)} />
        : <FiImage />}
    </span>
  );
}

export default function TruckLogoDropdown({ value = "", onChange, logos = [], logoUrl, disabled = false, movement, inPlace = false, dap = false, operationType }) {
  const id = useId();
  const rootRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);
  const searchRef = useRef({ text: "", time: 0 });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [position, setPosition] = useState({});
  const options = useMemo(() => {
    const context = { dap, operationType };
    return [...new Set(["", ...filterTruckLogos(logos, movement, context),
      ...(value && isTruckLogoAllowed(value, movement, context) ? [value] : [])])];
  }, [logos, value, movement, dap, operationType]);
  const label = getTruckLogoLabel;
  const selectedIndex = Math.max(0, options.indexOf(value));
  const isOpen = open && !disabled;

  useEffect(() => {
    if (!isOpen) return undefined;
    const closeOutside = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", closeOutside);
    document.addEventListener("touchstart", closeOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", closeOutside);
      document.removeEventListener("touchstart", closeOutside);
    };
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen || inPlace) return undefined;
    const update = () => {
      const rect = rootRef.current?.getBoundingClientRect();
      if (!rect) return;
      const below = window.innerHeight - rect.bottom - 12;
      const above = rect.top - 12;
      const upwards = below < 180 && above > below;
      const width = Math.min(Math.max(rect.width, 64), window.innerWidth - 16);
      setPosition({
        width,
        left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)) - rect.left,
        top: upwards ? undefined : "calc(100% + 4px)",
        bottom: upwards ? "calc(100% + 4px)" : undefined,
        maxHeight: Math.min(260, Math.max(0, upwards ? above : below)),
      });
    };
    update();
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [isOpen, inPlace]);

  useEffect(() => {
    if (isOpen) menuRef.current?.children[active]?.scrollIntoView?.({ block: "nearest" });
  }, [isOpen, active]);

  const choose = (file) => {
    setOpen(false);
    onChange?.(file);
    buttonRef.current?.focus();
  };
  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      if (isOpen) { event.preventDefault(); event.stopPropagation(); setOpen(false); }
      return;
    }
    if (event.key === "Tab") { setOpen(false); return; }
    if ((event.key === "Enter" || event.key === " ") && isOpen) {
      event.preventDefault(); choose(options[active] ?? ""); return;
    }
    if (["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) {
      event.preventDefault();
      setOpen(true);
      if (event.key === "Home") setActive(0);
      else if (event.key === "End") setActive(options.length - 1);
      else if (!isOpen) setActive(selectedIndex);
      else setActive((current) => (current + (event.key === "ArrowDown" ? 1 : -1) + options.length) % options.length);
      return;
    }
    if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey && event.key !== " ") {
      const time = Date.now();
      const text = (time - searchRef.current.time < 700 ? searchRef.current.text : "") + event.key.toLowerCase();
      searchRef.current = { text, time };
      const index = options.findIndex((file) => label(file).toLowerCase().startsWith(text));
      if (index >= 0) { event.preventDefault(); setOpen(true); setActive(index); }
    }
  };

  return (
    <div className={`truck-logo-dropdown${inPlace ? " in-place" : ""}`} ref={rootRef}>
      <button type="button" role="combobox" ref={buttonRef} className="truck-logo-dropdown-trigger"
        aria-label={`Logo del camión: ${label(value)}`} aria-haspopup="listbox" aria-expanded={isOpen}
        aria-controls={`${id}-list`} aria-activedescendant={isOpen ? `${id}-option-${active}` : undefined}
        disabled={disabled} title={label(value)} onKeyDown={handleKeyDown}
        onClick={() => { if (!isOpen) setActive(selectedIndex); setOpen(!isOpen); }}>
        <LogoThumbnail key={value} file={value} logoUrl={logoUrl} dap={dap} />
        {!disabled && <FiChevronDown className="truck-logo-dropdown-arrow" aria-hidden="true" />}
      </button>
      {isOpen && (
        <div id={`${id}-list`} className="truck-logo-dropdown-list" role="listbox" aria-label="Logos disponibles"
          ref={menuRef} style={inPlace ? { "--truck-logo-count": options.length } : position} onMouseDown={(event) => event.preventDefault()}>
          {options.map((file, index) => (
            <div key={file} id={`${id}-option-${index}`} role="option" aria-selected={file === value}
              aria-label={label(file)} title={label(file)}
              className={`truck-logo-dropdown-option${active === index ? " active" : ""}`}
              onMouseEnter={() => setActive(index)} onClick={() => choose(file)}>
              <LogoThumbnail key={file} file={file} logoUrl={logoUrl} dap={dap} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
